import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button, Input, Loader } from 'ncoded-component-library';
import FloatingBanner from 'components/FloatingBanner';
import useAddEventListener from 'hooks/useAddEventListener';
import { DataRoomBuySideGroup } from 'router/subrouters/DataRoom/models/DataRoomMember';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import MembersContext from 'router/subrouters/DataRoom/providers/Members/Members.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import useCallbackRef from 'hooks/useCallbackRef';
import useCloseOnEscape from 'hooks/useCloseOnEscape';
import { getArrayLength } from 'utils';

import './RenameGroupBanner.styles.scss';

const GROUP_NAME_MAX_LENGTH = 60;

type RenameGroupBannerProps = {
  className?: string;
  group: DataRoomBuySideGroup;
  // The group pill the banner is anchored to.
  anchor: ReactNode;
  // Dismisses the suggestion: "Not now", the close button, cancelling the
  // rename, or a successful rename. Nothing is stored, so it is only gone for
  // as long as this table stays open.
  onClose: () => void;
};

const RenameGroupBanner: React.FC<RenameGroupBannerProps> = (props) => {
  const { className, group, anchor, onClose } = props;

  const { _id: groupId, name, dataRoomMembers } = group;

  const [isRenaming, setIsRenaming] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [groupName, setGroupName] = useState(name);

  const [input, inputRef] = useCallbackRef<HTMLInputElement>();
  const [content, contentRef] = useCallbackRef<HTMLDivElement>();

  const { setBuySideGroups, setSellSideMembers } = useContext(MembersContext);
  const { popServerError } = useContext(PopNotificationsContext);

  const { t } = useTranslation();

  const classes = classNames(
    'rename-group-banner',
    { 'rename-group-banner--renaming': isRenaming },
    className,
  );

  const newName = groupName.trim();
  const canSave = !!newName && newName !== name && !isLoading;

  // The field opens focused and holding the current name, selected, so the
  // broker can type straight over it. The overlay mounts alongside it and can
  // take focus back, so the field claims it on the next tick.
  //
  // Focusing must not scroll: the field is portalled to the end of the
  // document and is only placed beside the pill a frame later, so on phone —
  // where the page itself scrolls — scrolling it into view drags the table
  // away and takes the banner with it.
  useEffect(() => {
    if (!isRenaming || !input) return;

    const timeout = setTimeout(() => input.focus({ preventScroll: true }), 0);

    return () => clearTimeout(timeout);
  }, [input, isRenaming]);

  useCloseOnEscape(onClose);

  // The rename step is a popup rather than a nudge, so a click anywhere but
  // the banner itself closes it. The suggestion nudge stays until "Not now"
  // or the close button is used, so it isn't lost to an unrelated click.
  const closeOnOutsideClick = useCallback(
    (event: PointerEvent) => {
      if (content?.contains(event.target as Node)) return;

      onClose();
    },
    [content, onClose],
  );

  useAddEventListener({
    event: 'pointerdown',
    handler: closeOnOutsideClick,
    capture: true,
    enabled: !!content && isRenaming,
  });

  const onSave = useCallback(async () => {
    try {
      setIsLoading(true);

      const { data } = await dataRoomApi.members.updateBuySideGroup(groupId, {
        name: newName,
      });

      setSellSideMembers(data.sellSideMembers);
      setBuySideGroups(data.buySideGroups);

      onClose();
    } catch (e) {
      // The name stays in the field: a rejected name (a duplicate on the deal,
      // for one) is fixed by editing it, not by starting over.
      popServerError(e);
    } finally {
      setIsLoading(false);
    }
  }, [
    groupId,
    newName,
    onClose,
    popServerError,
    setBuySideGroups,
    setSellSideMembers,
  ]);

  return (
    <FloatingBanner
      showOnPhablet
      showBannerNotification
      // The banner measures itself once per mount, and the rename step is a
      // good deal taller than the nudge it replaces — remounting is what gets
      // it measured and placed beside the pill again.
      key={isRenaming ? 'rename' : 'suggestion'}
      className={classes}
      anchor={anchor}
      onClose={onClose}
    >
      {isRenaming ? (
        <div className="rename-group-banner__rename" ref={contentRef}>
          <p className="rename-group-banner__title">{t('renameGroup')}</p>

          <Input
            ref={inputRef}
            value={groupName}
            maxLength={GROUP_NAME_MAX_LENGTH}
            onChange={(e) => setGroupName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' || !canSave) return;

              e.preventDefault();
              onSave();
            }}
          />

          <div className="actions">
            <Button variant="link" type="button" onClick={onClose}>
              {t('cancel')}
            </Button>

            <Button
              type="button"
              className="confirm"
              disabled={!canSave}
              onClick={onSave}
            >
              {t('save')}
            </Button>
          </div>

          {isLoading && (
            <div className="rename-group-banner__loader">
              <Loader />
            </div>
          )}
        </div>
      ) : (
        <div className="rename-group-banner__suggestion" ref={contentRef}>
          <p>
            {t('groupNowHasNumOfMembers', {
              groupName: name,
              count: getArrayLength(dataRoomMembers),
            })}
          </p>
          <p>{t('renameGroupToReflectMembers')}</p>

          <div className="actions">
            <Button
              variant="link"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
            >
              {t('notNow')}
            </Button>

            <Button
              className="confirm"
              onClick={(e) => {
                e.stopPropagation();
                setIsRenaming(true);
              }}
            >
              {t('renameGroup')}
            </Button>
          </div>
        </div>
      )}
    </FloatingBanner>
  );
};

export default RenameGroupBanner;
